import { Box, SvgIcon, Typography } from '@mui/material';
import BellIcon from 'assets/icons/bell.svg';
import FileEmptyIcon from 'assets/icons/file-empty.svg';
import MessagesIcon from 'assets/icons/messages.svg';
import PhoneSmsIcon from 'assets/icons/phone-sms-icon.svg';
import AppleDownload from 'assets/images/apple_download.svg';
import GoogleDownload from 'assets/images/google_download.svg';
import DesktopClassicIcon from 'mdi-react/DesktopClassicIcon';
import { object, string } from 'prop-types';
import React, { useEffect } from 'react';
import { QRCode } from 'react-qr-svg';
import { useDispatch } from 'react-redux';
import companyActions from 'store/app/entities/companies/action';
import { Translate } from 'utils/i18n';
import connector from './connector';
import AppIcon from 'assets/logos/AppIcon';

const Step = ({ title, description }) => (
  <li>
    <Box component="h3" sx={{ color: 'black' }}>
      {title}
    </Box>
    <Box sx={{ color: 'black' }}>{description}</Box>
  </li>
);

const Advantage = ({ icon, title, description }) => (
  <Box
    component="li"
    sx={{
      display: 'flex',
      mb: '3em',
      mr: '4em',
      maxWidth: 400,
      alignItems: 'flex-start',
      '& h3': {
        m: 0,
      },
    }}
  >
    <SvgIcon sx={{ fontSize: 18, marginRight: '5px', color: '#f8f9fd' }}>{icon}</SvgIcon>
    <div>
      {title}
      {description}
    </div>
  </Box>
);

const InvitationToPrint = ({ company, employee, magicLink }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (!company.logo) {
      dispatch(companyActions.loadOnboarding());
    }
  }, [company.logo, dispatch]);

  return (
    <Box
      data-mui-color-scheme="light"
      sx={{
        overflow: 'hidden',
        zoom: '75%',
        display: 'flex',
        flexDirection: 'column',
        padding: '50px',
        pageBreakBefore: 'always',
        color: 'black',
      }}
    >
      <Box
        sx={{
          '& img': {
            maxWidth: 100,
            maxHeight: 100,
          },
          marginBottom: '50px',
        }}
      >
        {(!company.logoDark && !company.logo) || (
          <img src={company.logoDark || company.logo} alt={company.name} />
        )}
      </Box>
      <Typography gutterBottom variant="h5">
        <Translate
          value="InvitationToPrint.headline"
          firstName={employee.firstName?.trim()}
          surName={employee.surName?.trim()}
          locale={employee.language}
        />
      </Typography>
      <Typography
        variant="h4"
        sx={{
          color: 'rgba(0, 0, 0, 0.6)',
          fontWeight: 'normal',
          marginBottom: '40px',
        }}
      >
        <Translate
          value="InvitationToPrint.subheading"
          company={company.name}
          locale={employee.language}
          dangerousHTML
        />
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <Box
          component="ol"
          sx={{
            counterReset: 'steps',
            '& h3': {
              mt: 0,
            },
            '& > li': {
              position: 'relative',
              background: '#f8f9fd',
              border: '1px solid #e1e2e6',
              borderRadius: '5px',
              p: '18px 12px 12px 57px',
              marginBottom: '14px',
              listStyleType: 'none',
            },
            '& > li:before': {
              margin: '12px',
              content: 'counter(steps)',
              counterIncrement: 'steps',
              position: 'absolute',
              top: 0,
              left: 0,
              border: '1px solid #e1e2e6',
              borderRadius: '50%',
              background: 'white',
              width: '2em',
              height: '2em',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        >
          <Step
            title={<Translate value="InvitationToPrint.Step_1_Title" locale={employee.language} />}
            description={
              <div>
                <Translate
                  value="InvitationToPrint.Step_1_Description"
                  locale={employee.language}
                  dangerousHTML
                />
                <Box
                  sx={{
                    position: 'absolute',
                    right: '12px',
                    top: '12px',
                    '& svg': {
                      width: 150,
                      height: 'auto',
                    },
                  }}
                >
                  <GoogleDownload />
                  <AppleDownload />
                </Box>
              </div>
            }
          />
          <Step
            title={<Translate value="InvitationToPrint.Step_2_Title" locale={employee.language} />}
            description={
              <div>
                <Box
                  component="ol"
                  sx={{
                    counterReset: 'substeps',
                    display: 'flex',
                    justifyContent: 'space-around',
                    p: 0,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      alignItems: 'flex-start',
                      mr: '1em',
                    },
                    '& > li > span:before': {
                      content: "counter(substeps)'. '",
                      counterIncrement: 'substeps',
                    },
                  }}
                >
                  <li>
                    <AppIcon style={{ width: 40, height: 40, marginRight: '1em' }} />
                    <Translate
                      value="InvitationToPrint.Step_2_Description_1"
                      locale={employee.language}
                    />
                  </li>
                  <li>
                    <Box
                      sx={{
                        width: 40,
                        color: theme => theme.palette.primary[50],
                        mr: '1em',
                        display: 'inline-flex',
                      }}
                    >
                      <PhoneSmsIcon />
                    </Box>
                    <Translate
                      value="InvitationToPrint.Step_2_Description_2"
                      locale={employee.language}
                    />
                  </li>
                </Box>
              </div>
            }
          />
          <Step
            title={<Translate value="InvitationToPrint.Step_3_Title" locale={employee.language} />}
            description={
              <Translate
                value="InvitationToPrint.Step_3_Description"
                locale={employee.language}
                dangerousHTML
              />
            }
          />
        </Box>
        <Box sx={{ width: 300, m: '2em' }}>
          <QRCode value={magicLink} size={200} level="H" />
        </Box>
      </Box>
      <Typography sx={{ mt: '2em' }} gutterBottom variant="h5">
        <Translate
          value="InvitationToPrint.Your colleagues"
          company={company.name}
          locale={employee.language}
        />
      </Typography>
      <Box
        sx={{
          color: 'rgba(0, 0, 0, 0.6)',
          mb: '1em',
          fontSize: '1.1em',
        }}
      >
        <Translate value="InvitationToPrint.You only need" locale={employee.language} />
      </Box>

      <Box
        component="ul"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          p: 0,
          m: 0,
        }}
      >
        {[MessagesIcon, BellIcon, FileEmptyIcon, DesktopClassicIcon].map((Icon, index) => (
          <Advantage
            key={`InvitationToPrint.Advantage_${index + 1}_Title`}
            icon={
              <Box sx={{ color: theme => theme.palette.primary[50], display: 'inline-flex' }}>
                <Icon />
              </Box>
            }
            title={
              <Translate
                value={`InvitationToPrint.Advantage_${index + 1}_Title`}
                locale={employee.language}
                tag="h3"
              />
            }
            description={
              <Translate
                style={{ color: 'rgba(0, 0, 0, 0.6)' }}
                value={`InvitationToPrint.Advantage_${index + 1}_Description`}
                locale={employee.language}
              />
            }
          />
        ))}
      </Box>
      {employee.code ? (
        <Box sx={{ textAlign: 'right' }}>
          <Translate
            style={{ color: 'rgba(0, 0, 0, 0.6)' }}
            value="InvitationToPrint.Employee code"
            locale={employee.language}
            code={employee.code}
          />
        </Box>
      ) : null}
    </Box>
  );
};
InvitationToPrint.propTypes = {
  company: object.isRequired,
  employee: object.isRequired,
  magicLink: string.isRequired,
};

export default connector(InvitationToPrint);
